import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  APPLE_AUTH_SCOPE,
  APPLE_CLIENT_ID,
  APPLE_REDIRECT_URI,
  GOOGLE_AUTH_SCOPE,
  GOOGLE_CLIENT_ID,
} from '../services/clientConfig';
import '../styles/Auth.css';

declare const google: any;
declare const AppleID: any;

const MICROSOFT_CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || '366258d5-f9f5-4591-81eb-22976b18775f';

const SignIn = () => {
  const navigate = useNavigate();
  const {
    login,
    googleLogin,
    appleLogin,
    microsoftLogin,
    initiateLinkedInWebSignIn,
    refreshAuthState,
  } = useAuth();
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [isGoogleReady, setIsGoogleReady] = useState(false);

  // State declarations (must be before any useEffect that calls setError)
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // ---------- Apple SDK initialization (eager, same as static HTML) ----------
  const initAppleSDK = useCallback(() => {
    if (typeof AppleID === 'undefined' || !AppleID.auth) return false;
    AppleID.auth.init({
      clientId: APPLE_CLIENT_ID,
      scope: APPLE_AUTH_SCOPE,
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    });
    return true;
  }, []);

  useEffect(() => {
    // Poll until Apple SDK is ready (same as static HTML's waitForAppleSDK)
    let attempts = 0;
    const interval = setInterval(() => {
      if (initAppleSDK()) {
        clearInterval(interval);
      } else if (++attempts > 40) {
        clearInterval(interval);
        console.warn('Apple SDK failed to load');
      }
    }, 250);
    return () => clearInterval(interval);
  }, [initAppleSDK]);

  // ---------- Google SDK initialization (same as static HTML) ----------
  useEffect(() => {
    let attempts = 0;
    const interval = setInterval(() => {
      if (!googleButtonRef.current) return;
      if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) {
        if (++attempts > 40) clearInterval(interval);
        return;
      }
      clearInterval(interval);
      // Render the Google button exactly like the static HTML's default button
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_AUTH_SCOPE,
        callback: async (response: any) => {
          if (!response?.credential) {
            setError('Google sign-in did not return a credential.');
            return;
          }
          try {
            const result = await googleLogin(response.credential);
            if (result?.accessToken || result?.token) {
              refreshAuthState();
              setTimeout(() => (window.location.href = '/'), 200);
              return;
            }
            if (result?.requiresPassword || result?.requiresLinkApproval) {
              setError(result.message || 'Google sign-in requires additional account setup.');
              return;
            }
            setError('Google sign-in failed.');
          } catch (err: any) {
            setError(err.message || 'Google sign-in failed');
          }
        },
        auto_select: false,
      });
      google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: Math.min(360, Math.floor(googleButtonRef.current.getBoundingClientRect().width || 360)),
      });
      setIsGoogleReady(true);
    }, 250);
    return () => clearInterval(interval);
  }, [googleLogin, refreshAuthState]);

  // ---------- Regular email/password login ----------
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!identifier || !password) {
      setError('Please enter both email/username and password');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await login({ email: identifier, password });
      refreshAuthState();
      setTimeout(() => (window.location.href = '/'), 200);
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
      setIsLoading(false);
    }
  };

  // ---------- Manual Google trigger (fallback) ----------
  const triggerGoogleSignIn = () => {
    if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) {
      setError('Google Sign-In is loading. Please try again.');
      return;
    }
    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_AUTH_SCOPE,
      callback: async (response: any) => {
        try {
          const result = await googleLogin(response.credential);
          if (result?.accessToken || result?.token) {
            refreshAuthState();
            setTimeout(() => (window.location.href = '/'), 200);
            return;
          }
          if (result?.requiresPassword || result?.requiresLinkApproval) {
            setError(result.message || 'Google sign-in requires additional account setup.');
            return;
          }
          setError('Google sign-in failed.');
        } catch (err: any) {
          setError(err.message || 'Google sign-in failed');
        }
      },
      ux_mode: 'popup',
      auto_select: false,
    });
    google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        const tmp = document.createElement('div');
        tmp.style.cssText = 'position:absolute;opacity:0;pointer-events:none;';
        document.body.appendChild(tmp);
        google.accounts.id.renderButton(tmp, { type: 'standard', theme: 'outline', size: 'large', shape: 'pill' });
        const clickable = tmp.querySelector<HTMLElement>('[role=button]') || tmp.firstElementChild;
        if (clickable) (clickable as HTMLElement).click();
        setTimeout(() => document.body.removeChild(tmp), 5000);
      }
    });
  };

  // ---------- Apple sign-in handler ----------
  const handleAppleSignIn = async () => {
    if (typeof AppleID === 'undefined' || !AppleID.auth) {
      setError('Apple Sign-In is loading. Please try again in a moment.');
      return;
    }
    try {
      const data = await AppleID.auth.signIn();
      const result = await appleLogin(data);
      if (result?.accessToken || result?.token) {
        refreshAuthState();
        setTimeout(() => (window.location.href = '/'), 200);
        return;
      }
      if (result?.requiresPassword || result?.requiresLinkApproval) {
        const identityToken =
          data?.authorization?.id_token ||
          data?.identityToken ||
          data?.id_token ||
          '';
        const appleUserId =
          (typeof data?.user === 'string' ? data.user : '') ||
          data?.userId ||
          '';

        navigate('/apple-setup', {
          state: {
            mode: result?.requiresLinkApproval ? 'link' : 'setup',
            appleResponse: data,
            identityToken,
            appleUserId,
            userInfo: result?.userInfo || {
              email: result?.userInfo?.email || '',
              name: result?.userInfo?.name || '',
              picture: result?.userInfo?.picture || '',
            },
            message: result?.message || 'Apple sign-in requires additional account setup.',
            linkingProcessId: result?.linkingProcessId || '',
            requiresPassword: !!result?.requiresPassword,
            requiresLinkApproval: !!result?.requiresLinkApproval,
          },
        });
        return;
      }
      setError('Apple sign-in failed. Please try again.');
    } catch (err: any) {
      if (err.error !== 'popup_closed_by_user') {
        setError(err.error || 'Apple sign-in failed. Please try again.');
      }
    }
  };

  // ---------- Microsoft sign-in handler ----------
  const loadMicrosoftSdk = async (): Promise<void> => {
    if ((window as any).msal?.PublicClientApplication) return;

    const src = 'https://cdn.jsdelivr.net/npm/@azure/msal-browser@2.38.3/lib/msal-browser.min.js';
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        if ((window as any).msal?.PublicClientApplication) resolve();
        else reject(new Error('MSAL loaded but unavailable'));
      };
      script.onerror = () => reject(new Error('Failed to load Microsoft SDK'));
      document.head.appendChild(script);
    });
  };

  const handleMicrosoftSignIn = async () => {
    try {
      setIsLoading(true);
      await loadMicrosoftSdk();

      const msalInstance = new (window as any).msal.PublicClientApplication({
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: window.location.origin,
        },
        cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
      });

      const response = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account',
      });

      if (!response.idToken) throw new Error('No Microsoft ID token returned.');

      const result = await microsoftLogin(response.idToken);
      if (result?.accessToken || result?.token) {
        refreshAuthState();
        setTimeout(() => (window.location.href = '/'), 200);
      } else if (result?.requiresPassword || result?.requiresLinkApproval) {
        setError(result.message || 'Microsoft sign-in requires additional account setup.');
      } else {
        setError('Microsoft sign-in failed.');
      }
    } catch (err: any) {
      const msg = String(err?.message || err?.errorMessage || '').toLowerCase();
      if (!msg.includes('cancel') && !msg.includes('user_cancelled')) {
        setError(err?.message || 'Microsoft sign-in failed.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- LinkedIn sign-in handler ----------
  const handleLinkedInSignIn = async () => {
    try {
      setIsLoading(true);
      const callbackUrl = `${window.location.origin}/linkedin/callback`;
      const { redirectUrl } = await initiateLinkedInWebSignIn(callbackUrl);
      if (!redirectUrl) throw new Error('LinkedIn sign-in URL was not returned.');
      window.location.assign(redirectUrl);
    } catch (err: any) {
      setError(err?.message || 'LinkedIn sign-in failed.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (event: React.MouseEvent) => {
    event.preventDefault();
    navigate('/forgot-password');
  };

  return (
    <div className="auth-page signin-page">
      <div className="auth-page-accent" aria-hidden="true" />
      <div className="page-header">
        <div className="logo">
          <img className="logo-img" src="/assets/logo2.webp" alt="Olitask" />
        </div>
        <h1>Welcome back</h1>
        <p className="subtitle">Sign in to continue to Olitask</p>
      </div>

      <div className="auth-card signin-card">
        <div className={`error-banner ${error ? 'show' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={`field ${error && !identifier ? 'has-error' : ''}`}>
            <label>Email or Username</label>
            <input
              type="text"
              placeholder="you@example.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
            <div className="error-msg">Please enter your email or username</div>
          </div>

          <div className={`field ${error && !password ? 'has-error' : ''}`}>
            <div className="field-meta">
              <label>Password</label>
              <button type="button" onClick={handleForgotPassword} className="forgot-link">
                Forgot password?
              </button>
            </div>
            <div className="pw-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="pw-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
            <div className="error-msg">Please enter your password</div>
          </div>

          <div className="btn-row">
            <Link to="/" className="btn btn-cancel" style={{ backgroundColor: '#dc2626', borderColor: '#dc2626', color: 'white' }}>
              Cancel
            </Link>
            <button type="submit" className="btn btn-login" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>

        <div className="footer-links">
          <button className="footer-link-button" type="button" onClick={handleForgotPassword}>
            Forgot password?
          </button>
          <span className="dot">.</span>
          <span>New here?</span>
          <Link to="/signup">Sign Up</Link>
        </div>

        <div className="divider">or continue with</div>

        <div className="sso-google-rendered" ref={googleButtonRef} />
        {!isGoogleReady && (
          <button className="sso-btn sso-google" type="button" onClick={triggerGoogleSignIn}>
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>
        )}

        <button className="sso-btn sso-apple" type="button" onClick={handleAppleSignIn}>
          <svg width="18" height="18" viewBox="0 0 814 1000" fill="currentColor">
            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.7 134.4-317.5 266.5-317.5 99.7 0 184.5 66.1 244.3 66.1 56.7 0 146.1-70.2 260.6-70.2 42.2 0 168.2 3.8 259.4 136.5zm-234-181.5c47.1-56.6 79.5-134.9 79.5-213.1 0-10.9-.9-21.9-2.9-30.9-75.5 2.9-166.2 50.4-220.9 116.7-41.1 47.8-81.1 126.1-81.1 205.9 0 12.2 2 24.5 2.9 28.5 4.8.9 12.7 1.9 20.7 1.9 67.5 0 154.5-45.4 201.8-108z" />
          </svg>
          Continue with Apple
        </button>

        <button className="sso-btn sso-microsoft" type="button" onClick={handleMicrosoftSignIn}>
          <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" rx="1" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" rx="1" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" rx="1" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" rx="1" />
          </svg>
          Sign in with Microsoft
        </button>

        <button className="sso-btn sso-linkedin" type="button" onClick={handleLinkedInSignIn}>
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          Sign in with LinkedIn
        </button>
      </div>
    </div>
  );
};

export default SignIn;
